import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { ChatMessage } from "../../types";
import { Icon, Banner } from "./ui";
import Lightbox from "./Lightbox";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function Bubble({
  msg, mine, isRu, photoUrl, renderUnder, onOpenImage,
}: {
  msg: ChatMessage; mine: boolean; isRu: boolean;
  photoUrl: (m: ChatMessage) => string;
  renderUnder?: (m: ChatMessage) => React.ReactNode;
  onOpenImage: (src: string) => void;
}) {
  const { t } = useTranslation();
  const time = new Date(msg.created_at).toLocaleTimeString(isRu ? "ru-RU" : "en-US", { hour: "2-digit", minute: "2-digit" });
  const exTitle = isRu ? msg.exercise_title_ru : (msg.exercise_title_en || msg.exercise_title_ru);
  const statusLabel = msg.status === "passed" ? t("inbox.passed") : msg.status === "failed" ? t("inbox.needsWork") : t("exercise.statusSubmitted");
  const statusCls = msg.status === "passed" ? "badge-success" : msg.status === "failed" ? "badge-amber" : "badge-info";

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start", marginBottom: 8 }}>
      {!mine && msg.is_admin && <span className="t-xs muted-2" style={{ marginBottom: 3, paddingLeft: 4 }}>Anya</span>}
      <div style={{
        maxWidth: "82%", padding: msg.has_photo ? 4 : "8px 12px",
        borderRadius: mine ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
        background: msg.is_admin ? "var(--violet-tint)" : "var(--accent-tint)",
        border: `1px solid ${msg.is_admin ? "var(--violet-glow)" : "var(--accent-glow)"}`,
        color: "var(--text-1)",
      }}>
        {msg.kind === "work" && (
          <div className="row" style={{ gap: 6, padding: msg.has_photo ? "4px 8px 6px" : "0 0 6px", flexWrap: "wrap" }}>
            <span className="badge badge-violet"><Icon name="brush" size={11} /> {t("exercise.heading")}{exTitle ? `: ${exTitle}` : ""}</span>
            {msg.status && <span className={`badge ${statusCls}`}>{statusLabel}</span>}
          </div>
        )}
        {msg.has_photo && (
          <img src={photoUrl(msg)} alt="photo" loading="lazy"
            onClick={() => onOpenImage(photoUrl(msg))}
            style={{ width: "100%", maxWidth: 240, borderRadius: 12, display: "block", cursor: "zoom-in", marginBottom: msg.body ? 6 : 0 }} />
        )}
        {msg.body && (
          <p className="t-sm" style={{ margin: msg.has_photo ? "0 8px 6px" : 0, lineHeight: 1.45, fontWeight: 500, whiteSpace: "pre-wrap" }}>
            {msg.body}
          </p>
        )}
        {msg.reviewer_note && (
          <p className="t-xs" style={{ margin: msg.has_photo ? "0 8px 6px" : "4px 0 0", color: "var(--violet-text)", fontWeight: 600 }}>
            {msg.reviewer_note}
          </p>
        )}
        {renderUnder && <div style={{ padding: msg.has_photo ? "0 6px 6px" : 0 }}>{renderUnder(msg)}</div>}
      </div>
      <span className="t-xs muted-2" style={{ marginTop: 3, paddingInline: 4 }}>{time}</span>
    </div>
  );
}

export default function ChatThread({
  messages, isRu, senderIsAdmin, onSend, photoUrl, placeholder, emptyHint, renderUnder, composer,
}: {
  messages: ChatMessage[];
  isRu: boolean;
  senderIsAdmin: boolean;
  onSend: (text: string, photoBase64?: string) => Promise<void>;
  photoUrl: (m: ChatMessage) => string;
  placeholder: string;
  emptyHint?: string;
  renderUnder?: (m: ChatMessage) => React.ReactNode;
  /** Replaces the input row — e.g. an upgrade card when writing is not allowed. */
  composer?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f)); setError(null);
  };

  const send = async () => {
    if ((!text.trim() && !file) || sending) return;
    setSending(true); setError(null);
    try {
      const photo = file ? await fileToDataUrl(file) : undefined;
      await onSend(text.trim(), photo);
      setText(""); setFile(null); setPreview(null);
    } catch (e) {
      const m = e instanceof Error ? e.message : "";
      setError(m.includes("413") ? t("chat.tooLarge") : (m || t("common.error")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", marginBottom: 10 }}>
        {messages.length > 0 ? (
          <>
            {messages.map((m) => (
              <Bubble key={m.id} msg={m} mine={m.is_admin === senderIsAdmin} isRu={isRu} photoUrl={photoUrl} renderUnder={renderUnder} onOpenImage={setLightbox} />
            ))}
            <div ref={endRef} />
          </>
        ) : (
          emptyHint && <p className="t-xs muted-2" style={{ marginTop: 8, textAlign: "center" }}>{emptyHint}</p>
        )}
      </div>

      {error && <div style={{ marginBottom: 8 }}><Banner kind="error">{error}</Banner></div>}

      {preview && (
        <div style={{ position: "relative", marginBottom: 8, display: "inline-block" }}>
          <img src={preview} alt="preview" style={{ maxHeight: 90, borderRadius: 10, display: "block" }} />
          <button className="icon-btn" onClick={() => { setFile(null); setPreview(null); }}
            style={{ position: "absolute", top: 4, right: 4, width: 26, height: 26, borderRadius: 8 }}>
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      {composer ?? (
      <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
        <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: "none" }} />
        <button className="btn btn-glass" onClick={() => fileRef.current?.click()} aria-label={t("chat.attach")}
          style={{ minHeight: 44, paddingInline: 12, flexShrink: 0 }}>
          <Icon name="image" size={18} />
        </button>
        <textarea className="textarea" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2} style={{ flex: 1, minHeight: 44, resize: "none" }} placeholder={placeholder} />
        <button className="btn btn-violet" onClick={send} disabled={sending || (!text.trim() && !file)}
          style={{ minHeight: 44, paddingInline: 14, flexShrink: 0 }}>
          {sending ? <span className="spinner" /> : <Icon name="send" size={18} />}
        </button>
      </div>
      )}
    </div>
  );
}
